/**
 * Zusatzdokumente / Prüflisten je Mandant: hochgeladene Vordrucke (PDF etc.), die beim Druck eines
 * Werkstattauftrags zum Mitdrucken angeboten werden -- z. B. UVV-Prüfung, Maschinenankunftsprotokoll,
 * Elektro-Prüfung. In der Administration gepflegt, beim Werkstattauftrag angeboten.
 *
 * Speicherung des Inhalts als data:-URL (wie die Maschinen-Anhänge); in Listen wird der Inhalt NICHT
 * mitgeliefert (zu groß) -- erst beim gezielten Abruf/Druck.
 */

export type AttachmentDocumentCategory = 'checklist' | 'protocol' | 'form' | 'other';

export const attachmentDocumentCategoryLabels: Readonly<Record<AttachmentDocumentCategory, string>> = {
  checklist: 'Prüfliste',
  protocol: 'Protokoll',
  form: 'Formular',
  other: 'Sonstiges',
};

export const attachmentDocumentCategories: readonly AttachmentDocumentCategory[] = [
  'checklist',
  'protocol',
  'form',
  'other',
];

export interface AttachmentDocumentRecord {
  readonly id: string;
  readonly title: string;
  readonly category: AttachmentDocumentCategory;
  readonly mimeType: string;
  readonly byteSize: number;
  /** Immer beim Werkstattauftrag vorschlagen (unabhängig vom Vorgangstext). */
  readonly alwaysOffer: boolean;
  /** Vorschlag, wenn eines dieser Stichwörter im Vorgangstext vorkommt (z. B. "UVV", "Elektro"). */
  readonly keywords: readonly string[];
  readonly createdAt: string;
  /** data:-URL; in Listen leer, nur bei getDocument gefüllt. */
  readonly content: string;
}

export interface AddAttachmentDocumentInput {
  readonly tenantId: string;
  readonly title: string;
  readonly category: AttachmentDocumentCategory;
  readonly mimeType: string;
  readonly byteSize: number;
  readonly alwaysOffer: boolean;
  readonly keywords: readonly string[];
  readonly content: string;
}

export interface UpdateAttachmentDocumentInput {
  readonly tenantId: string;
  readonly documentId: string;
  readonly title: string;
  readonly category: AttachmentDocumentCategory;
  readonly alwaysOffer: boolean;
  readonly keywords: readonly string[];
}

export interface AttachmentDocumentGateway {
  /** Metadaten aller Zusatzdokumente des Mandanten (ohne Inhalt). */
  listDocuments(tenantId: string): Promise<readonly AttachmentDocumentRecord[]>;
  /** Ein Dokument inkl. Inhalt (data:-URL) -- zum Drucken/Herunterladen. */
  getDocument(tenantId: string, documentId: string): Promise<AttachmentDocumentRecord>;
  addDocument(input: AddAttachmentDocumentInput): Promise<AttachmentDocumentRecord>;
  /** Metadaten/Regel ändern, ohne die Datei neu hochzuladen. */
  updateDocument(input: UpdateAttachmentDocumentInput): Promise<AttachmentDocumentRecord>;
  removeDocument(tenantId: string, documentId: string): Promise<void>;
}

/** Stichwörter aus einer Freitexteingabe (Komma/Zeilenumbruch-getrennt) normalisieren. */
export function parseAttachmentKeywords(input: string): string[] {
  return Array.from(
    new Set(
      input
        .split(/[,\n;]/)
        .map((part) => part.trim())
        .filter(Boolean),
    ),
  );
}

/**
 * Wird das Dokument beim Werkstattauftrag vorgeschlagen? Ja, wenn "immer" gesetzt ist oder eines
 * der Stichwörter (case-insensitive) im übergebenen Vorgangstext vorkommt. Der Aufrufer baut den
 * Vorgangstext (Titel + Positionen) selbst zusammen -- kein Bezug auf das Orders-Modell hier.
 */
export function isAttachmentDocumentSuggested(
  doc: Pick<AttachmentDocumentRecord, 'alwaysOffer' | 'keywords'>,
  orderText: string,
): boolean {
  if (doc.alwaysOffer) return true;
  const haystack = orderText.toLowerCase();
  return doc.keywords.some((keyword) => {
    const needle = keyword.trim().toLowerCase();
    return needle.length > 0 && haystack.includes(needle);
  });
}
